import { getSafeApiUrl, getSafeRoutePattern } from './sanitization';
import { isRateLimited } from './errorReporting';

interface ApiTelemetryOptions {
  method?: string;
  url?: string;
  status?: number;
  durationMs?: number;
  success: boolean;
}

interface ApiTelemetryPayload {
  api: {
    method: string;
    path: string;
    status?: number;
    durationMs: number;
    success: boolean;
  };
  page: {
    route: string;
  };
  app: {
    environment: string;
    gitSha: string;
  };
  timestamp: string;
}

const endpoint =
  import.meta.env.VITE_API_TELEMETRY_ENDPOINT?.trim() ||
  '/telemetry/api-metrics';

const isEnabled =
  import.meta.env.PROD &&
  import.meta.env.VITE_API_TELEMETRY_ENABLED !== 'false';

export const getTelemetryStartTime = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

export const getTelemetryDurationMs = (startTime: number | undefined): number => {
  if (typeof startTime !== 'number' || !Number.isFinite(startTime)) {
    return 0;
  }

  return Math.max(0, Math.round(getTelemetryStartTime() - startTime));
};

const normalizeMethod = (method: string | undefined): string =>
  (method || 'GET').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 12) || 'GET';

const normalizeStatus = (status: number | undefined): number | undefined => {
  if (typeof status !== 'number' || !Number.isFinite(status)) {
    return undefined;
  }

  const rounded = Math.floor(status);
  return rounded >= 0 && rounded <= 599 ? rounded : undefined;
};

const normalizeDuration = (durationMs: number | undefined): number => {
  if (typeof durationMs !== 'number' || !Number.isFinite(durationMs)) {
    return 0;
  }

  return Math.min(Math.max(0, Math.round(durationMs)), 300_000);
};

// Exported for unit testing (mirrors the build*ForTest convention in server.mjs).
export const buildPayload = (options: ApiTelemetryOptions): ApiTelemetryPayload => ({
  api: {
    method: normalizeMethod(options.method),
    path: getSafeApiUrl(options.url) || '/api',
    status: normalizeStatus(options.status),
    durationMs: normalizeDuration(options.durationMs),
    success: options.success,
  },
  page: {
    route: getSafeRoutePattern(),
  },
  app: {
    environment:
      import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'unknown',
    gitSha: import.meta.env.VITE_GIT_SHA || 'local',
  },
  timestamp: new Date().toISOString(),
});

export const reportApiMetric = (options: ApiTelemetryOptions): void => {
  const payload = buildPayload(options);

  if (!isEnabled) {
    if (import.meta.env.DEV) {
      console.info('[api-telemetry]', payload);
    }
    return;
  }

  if (isRateLimited()) {
    if (import.meta.env.DEV) {
      console.warn('[api-telemetry] Rate limit exceeded, dropping metric');
    }
    return;
  }

  void fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
    credentials: 'omit',
  }).catch(() => {
    // Telemetry must never affect application behavior.
  });
};
